export interface ImportColumn {
  field: string;
  label: string;
  required: boolean;
  type: 'text' | 'email' | 'number' | 'date' | 'select';
  options?: string[];
  validation?: (value: any) => string | null;
}

export interface ImportMapping {
  [csvColumn: string]: string; // Maps CSV column to field name
}

export interface ImportValidationError {
  row: number;
  column: string;
  value: any;
  error: string;
}

export interface ImportPreview {
  headers: string[];
  sampleData: any[][];
  totalRows: number;
}

export interface ImportResult {
  success: boolean;
  imported: number;
  errors: ImportValidationError[];
  duplicates: number;
  message?: string;
}

class ImportService {
  // Define available columns for different import types
  private studentColumns: ImportColumn[] = [
    { field: 'id', label: 'ID/RUT', required: true, type: 'text' },
    { field: 'name', label: 'Nombre Completo', required: true, type: 'text' },
    { field: 'firstName', label: 'Nombre', required: false, type: 'text' },
    { field: 'lastName', label: 'Apellido', required: false, type: 'text' },
    { field: 'course', label: 'Curso', required: true, type: 'text' },
    { field: 'email', label: 'Email', required: false, type: 'email' },
    { field: 'birthDate', label: 'Fecha de Nacimiento', required: false, type: 'date' },
    { field: 'gender', label: 'Género', required: false, type: 'select', options: ['M', 'F', 'Otro'] }
  ];

  private teacherColumns: ImportColumn[] = [
    { field: 'username', label: 'Nombre de Usuario', required: true, type: 'text' },
    { field: 'email', label: 'Email', required: true, type: 'email' },
    { field: 'firstName', label: 'Nombre', required: true, type: 'text' },
    { field: 'lastName', label: 'Apellido', required: true, type: 'text' },
    { field: 'role', label: 'Rol', required: true, type: 'select', options: ['admin', 'teacher', 'viewer'] },
    { field: 'courses', label: 'Cursos Asignados', required: false, type: 'text' },
    { field: 'phone', label: 'Teléfono', required: false, type: 'text' }
  ];

  async parseFile(file: File): Promise<{ success: boolean; preview?: ImportPreview; message?: string }> {
    try {
      const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
      
      if (isExcel) {
        return await this.parseExcelFile(file);
      } else {
        return await this.parseCSVFile(file);
      }
    } catch (error) {
      return {
        success: false,
        message: 'Error al procesar el archivo'
      };
    }
  }

  async parseCSVFile(file: File): Promise<{ success: boolean; preview?: ImportPreview; message?: string }> {
    try {
      const text = await this.readFileAsText(file);
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        return {
          success: false,
          message: 'El archivo está vacío'
        };
      }

      const headers = this.parseCSVLine(lines[0]);
      const sampleData: any[][] = [];
      
      // Take first 5 rows as sample
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        sampleData.push(this.parseCSVLine(lines[i]));
      }

      return {
        success: true,
        preview: {
          headers,
          sampleData,
          totalRows: lines.length - 1 // Exclude header
        }
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error al leer el archivo CSV'
      };
    }
  }

  async parseExcelFile(file: File): Promise<{ success: boolean; preview?: ImportPreview; message?: string }> {
    try {
      // Dynamically import xlsx to avoid bundle size issues
      const XLSX = await import('xlsx');
      
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get first worksheet
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        return {
          success: false,
          message: 'El archivo Excel no contiene hojas de trabajo'
        };
      }

      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      
      if (jsonData.length === 0) {
        return {
          success: false,
          message: 'La hoja de trabajo está vacía'
        };
      }

      const headers = (jsonData[0] as any[]).map(h => h?.toString() || '');
      const sampleData: any[][] = [];
      
      // Take first 5 rows as sample (excluding header)
      for (let i = 1; i < Math.min(6, jsonData.length); i++) {
        const row = (jsonData[i] as any[]).map(cell => cell?.toString() || '');
        sampleData.push(row);
      }

      return {
        success: true,
        preview: {
          headers,
          sampleData,
          totalRows: jsonData.length - 1 // Exclude header
        }
      };
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      return {
        success: false,
        message: 'Error al leer el archivo Excel. Asegúrate de que sea un archivo .xlsx válido'
      };
    }
  }

  async validateImportData(
    file: File, 
    mapping: ImportMapping, 
    type: 'students' | 'teachers'
  ): Promise<{ success: boolean; errors?: ImportValidationError[]; totalRows?: number; message?: string }> {
    try {
      const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
      let headers: string[];
      let dataRows: any[][];

      if (isExcel) {
        const XLSX = await import('xlsx');
        const arrayBuffer = await this.readFileAsArrayBuffer(file);
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        
        if (jsonData.length <= 1) {
          return {
            success: false,
            message: 'El archivo debe contener al menos una fila de datos'
          };
        }

        headers = (jsonData[0] as any[]).map(h => h?.toString() || '');
        dataRows = jsonData.slice(1).map(row => (row as any[]).map(cell => cell?.toString() || ''));
      } else {
        const text = await this.readFileAsText(file);
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length <= 1) {
          return {
            success: false,
            message: 'El archivo debe contener al menos una fila de datos'
          };
        }

        headers = this.parseCSVLine(lines[0]);
        dataRows = lines.slice(1).map(line => this.parseCSVLine(line));
      }

      const columns = type === 'students' ? this.studentColumns : this.teacherColumns;
      const errors: ImportValidationError[] = [];

      // Validate mapping
      const requiredFields = columns.filter(col => col.required).map(col => col.field);
      const mappedFields = Object.values(mapping);
      
      for (const field of requiredFields) {
        if (!mappedFields.includes(field)) {
          errors.push({
            row: 0,
            column: field,
            value: '',
            error: `Campo requerido '${field}' no está mapeado`
          });
        }
      }

      // Validate data rows
      dataRows.forEach((rowData, index) => {
        const rowNumber = index + 2; // +2 because we start from 1 and skip header
        const rowObject: any = {};

        // Map columns to fields
        headers.forEach((header, headerIndex) => {
          const field = mapping[header];
          if (field && rowData[headerIndex] !== undefined) {
            rowObject[field] = rowData[headerIndex];
          }
        });

        // Validate each field
        for (const column of columns) {
          const value = rowObject[column.field];
          
          // Check required fields
          if (column.required && (!value || value.toString().trim() === '')) {
            errors.push({
              row: rowNumber,
              column: column.field,
              value: value,
              error: `Campo requerido '${column.label}' está vacío`
            });
            continue;
          }

          // Type validation
          if (value && value.toString().trim() !== '') {
            const validationError = this.validateFieldValue(value, column);
            if (validationError) {
              errors.push({
                row: rowNumber,
                column: column.field,
                value: value,
                error: validationError
              });
            }
          }
        }
      });

      return {
        success: errors.length === 0,
        errors,
        totalRows: dataRows.length
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error al validar los datos'
      };
    }
  }

  async importData(
    file: File,
    mapping: ImportMapping,
    type: 'students' | 'teachers',
    organizationId: string
  ): Promise<ImportResult> {
    try {
      // First validate
      const validation = await this.validateImportData(file, mapping, type);
      
      if (!validation.success) {
        return {
          success: false,
          imported: 0,
          errors: validation.errors || [],
          duplicates: 0,
          message: validation.message
        };
      }

      const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
      let headers: string[];
      let dataRows: any[][];

      if (isExcel) {
        const XLSX = await import('xlsx');
        const arrayBuffer = await this.readFileAsArrayBuffer(file);
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        
        headers = (jsonData[0] as any[]).map(h => h?.toString() || '');
        dataRows = jsonData.slice(1).map(row => (row as any[]).map(cell => cell?.toString() || ''));
      } else {
        const text = await this.readFileAsText(file);
        const lines = text.split('\n').filter(line => line.trim());
        headers = this.parseCSVLine(lines[0]);
        dataRows = lines.slice(1).map(line => this.parseCSVLine(line));
      }
      
      let imported = 0;
      let duplicates = 0;
      const errors: ImportValidationError[] = [];

      // Process each row
      dataRows.forEach(async (rowData, index) => {
        const rowNumber = index + 2; // +2 because we start from 1 and skip header
        const rowObject: any = {};

        // Map columns to fields
        headers.forEach((header, headerIndex) => {
          const field = mapping[header];
          if (field && rowData[headerIndex] !== undefined) {
            rowObject[field] = rowData[headerIndex];
          }
        });

        try {
          // Import to real database
          if (type === 'students') {
            await this.importStudent(rowObject, organizationId);
          } else {
            await this.importTeacher(rowObject, organizationId);
          }
          imported++;
        } catch (error: any) {
          if (error.message.includes('duplicate')) {
            duplicates++;
          } else {
            errors.push({
              row: rowNumber,
              column: 'general',
              value: rowObject,
              error: error.message
            });
          }
        }
      });

      return {
        success: true,
        imported,
        errors,
        duplicates
      };
    } catch (error) {
      return {
        success: false,
        imported: 0,
        errors: [],
        duplicates: 0,
        message: 'Error durante la importación'
      };
    }
  }

  getAvailableColumns(type: 'students' | 'teachers'): ImportColumn[] {
    return type === 'students' ? this.studentColumns : this.teacherColumns;
  }

  private async readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file, 'UTF-8');
    });
  }

  private async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsArrayBuffer(file);
    });
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  }

  private validateFieldValue(value: any, column: ImportColumn): string | null {
    const strValue = value.toString().trim();

    switch (column.type) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(strValue)) {
          return 'Formato de email inválido';
        }
        break;

      case 'number':
        if (isNaN(Number(strValue))) {
          return 'Debe ser un número válido';
        }
        break;

      case 'date':
        const date = new Date(strValue);
        if (isNaN(date.getTime())) {
          return 'Formato de fecha inválido';
        }
        break;

      case 'select':
        if (column.options && !column.options.includes(strValue)) {
          return `Valor debe ser uno de: ${column.options.join(', ')}`;
        }
        break;
    }

    // Custom validation
    if (column.validation) {
      return column.validation(value);
    }

    return null;
  }

  private async importStudent(data: any, organizationId: string): Promise<void> {
    const { databaseService } = await import('./databaseService');
    
    // Check for duplicates
    const existingStudents = await databaseService.getStudents(organizationId);
    if (existingStudents.some(s => s.id === data.id)) {
      throw new Error('duplicate: Student with this ID already exists');
    }

    // Create student
    await databaseService.createStudent(organizationId, {
      id: data.id,
      name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
      firstName: data.firstName,
      lastName: data.lastName,
      course: data.course,
      email: data.email,
      birthDate: data.birthDate,
      gender: data.gender
    });
  }

  private async importTeacher(data: any, organizationId: string): Promise<void> {
    const { databaseService } = await import('./databaseService');
    
    // Check for duplicates
    const existingTeachers = await databaseService.getTeachers(organizationId);
    if (existingTeachers.some(t => t.email === data.email)) {
      throw new Error('duplicate: Teacher with this email already exists');
    }

    // Create teacher
    await databaseService.createTeacher(organizationId, {
      username: data.username,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role || 'teacher',
      courses: data.courses ? data.courses.split(',').map((c: string) => c.trim()) : [],
      phone: data.phone,
      isActive: true,
      mustChangePassword: true,
      password: data.password || this.generateRandomPassword()
    });
  }

  private generateRandomPassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }
}

export const importService = new ImportService();
export default importService;